import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Heart,
  Loader2,
  Sparkles,
  Video,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Id } from "~/convex/_generated/dataModel";
import { Progress } from "@/components/ui/progress";
import { api } from "~/convex/_generated/api";
import { useAction } from "convex/react";
import { useToast } from "@/components/ui/use-toast";

interface Hook {
  text: string;
  category: string;
  reasoning: string;
  potentialPaths?: string[];
}

type VideoFormat = "shorts" | "long-form";

interface ScriptSection {
  id: string;
  title: string;
  type:
    | "hook"
    | "intro"
    | "main"
    | "cta"
    | "problem"
    | "solution"
    | "proof"
    | "transition"
    | "conclusion";
  content: string;
  variations: string[];
  currentVariationIndex: number;
  isGenerating?: boolean;
  description?: string;
}

interface FloatingButtonPosition {
  x: number;
  y: number;
  visible: boolean;
  sectionId: string;
}

interface ScriptGenerationProps {
  selectedHook: Hook | null;
  creatorId: Id<"exp__creatorProfile">;
  onBack: () => void;
}

const shortsSections: Omit<ScriptSection, "content" | "variations">[] = [
  {
    id: "1",
    title: "Hook",
    type: "hook",
    currentVariationIndex: 0,
    description: "Grab attention in the first 3 seconds",
  },
  {
    id: "2",
    title: "Problem/Question",
    type: "problem",
    currentVariationIndex: 0,
    description: "Present the core problem or question",
  },
  {
    id: "3",
    title: "Solution/Answer",
    type: "solution",
    currentVariationIndex: 0,
    description: "Deliver the main value or answer",
  },
  {
    id: "4",
    title: "Call to Action",
    type: "cta",
    currentVariationIndex: 0,
    description: "Encourage engagement and follow",
  },
];

const longFormSections: Omit<ScriptSection, "content" | "variations">[] = [
  {
    id: "1",
    title: "Hook",
    type: "hook",
    currentVariationIndex: 0,
    description: "Compelling opening to grab attention",
  },
  {
    id: "2",
    title: "Introduction",
    type: "intro",
    currentVariationIndex: 0,
    description: "Welcome viewers and set expectations",
  },
  {
    id: "3",
    title: "Problem Setup",
    type: "problem",
    currentVariationIndex: 0,
    description: "Establish the problem or topic context",
  },
  {
    id: "4",
    title: "Main Content",
    type: "main",
    currentVariationIndex: 0,
    description: "Core educational or entertainment content",
  },
  {
    id: "5",
    title: "Proof/Examples",
    type: "proof",
    currentVariationIndex: 0,
    description: "Evidence, examples, or demonstrations",
  },
  {
    id: "6",
    title: "Transition",
    type: "transition",
    currentVariationIndex: 0,
    description: "Bridge to conclusion and next steps",
  },
  {
    id: "7",
    title: "Conclusion",
    type: "conclusion",
    currentVariationIndex: 0,
    description: "Summarize key points and takeaways",
  },
  {
    id: "8",
    title: "Call to Action",
    type: "cta",
    currentVariationIndex: 0,
    description: "Encourage engagement and subscription",
  },
];

export const ScriptGeneration = ({
  selectedHook,
  creatorId,
  onBack,
}: ScriptGenerationProps) => {
  const { toast } = useToast();
  const [videoFormat, setVideoFormat] = useState<VideoFormat | null>(null);
  const [scriptSections, setScriptSections] = useState<ScriptSection[]>([]);
  const [selectedText, setSelectedText] = useState("");
  const [generationGuidance, setGenerationGuidance] = useState("");
  const [activeSection, setActiveSection] = useState<string>("1");
  const [completedSections, setCompletedSections] = useState<Set<string>>(
    new Set(["1"]),
  );
  const [buttonPosition, setButtonPosition] = useState<FloatingButtonPosition>({
    x: 0,
    y: 0,
    visible: false,
    sectionId: "",
  });
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Convex actions
  const generateScript = useAction(
    api.exp__creatorInsights.exp__generateScript,
  );
  const rewriteSection = useAction(api.generate.exp__rewriteSection);

  // Initialize sections when format is selected
  useEffect(() => {
    if (videoFormat) {
      const initialSections =
        videoFormat === "shorts" ? shortsSections : longFormSections;
      setScriptSections(
        initialSections.map((section) =>
          section.id === "1"
            ? {
                ...section,
                content: selectedHook?.text || "",
                variations: selectedHook?.text ? [selectedHook.text] : [],
              }
            : {
                ...section,
                content: "",
                variations: [],
              },
        ),
      );
    }
  }, [videoFormat, selectedHook]);

  const handleSelection = (sectionId: string) => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      setButtonPosition((prev) => ({ ...prev, visible: false }));
      setSelectedText("");
      setGenerationGuidance("");
      return;
    }

    const range = selection.getRangeAt(0);
    const text = selection.toString().trim();

    if (text) {
      const rect = range.getBoundingClientRect();
      setSelectedText(text);
      setButtonPosition({
        x: rect.left + rect.width / 2,
        y: rect.top - 80,
        visible: true,
        sectionId,
      });
    } else {
      setButtonPosition((prev) => ({ ...prev, visible: false }));
      setSelectedText("");
      setGenerationGuidance("");
    }
  };

  const regenerateSelection = async () => {
    if (!selectedText || !buttonPosition.sectionId) return;

    setIsRegenerating(true);
    try {
      const section = scriptSections.find(
        (s) => s.id === buttonPosition.sectionId,
      );
      if (!section) return;

      const newText = await rewriteSection({
        creatorId,
        originalText: selectedText,
        guidance: generationGuidance || undefined,
        sectionType: section.type,
        fullSectionContent: section.content,
      });

      setScriptSections((prev) =>
        prev.map((section) => {
          if (section.id === buttonPosition.sectionId) {
            const updatedContent = section.content.replace(
              selectedText,
              newText,
            );
            return {
              ...section,
              content: updatedContent,
              variations: [...section.variations, updatedContent],
              currentVariationIndex: section.variations.length,
            };
          }
          return section;
        }),
      );

      toast({
        title: "Text Regenerated",
        description: "The selected text has been rewritten successfully.",
      });
    } catch (error) {
      console.error("Error regenerating text:", error);
      toast({
        title: "Regeneration Failed",
        description:
          "Failed to regenerate the selected text. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRegenerating(false);
      setButtonPosition((prev) => ({ ...prev, visible: false }));
      setSelectedText("");
      setGenerationGuidance("");
    }
  };

  const generateSectionContent = async (sectionId: string) => {
    const section = scriptSections.find((s) => s.id === sectionId);
    if (!section || !videoFormat) return;

    // Mark section as generating
    setScriptSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, isGenerating: true } : s)),
    );

    try {
      const previousSections = scriptSections
        .filter((s) => parseInt(s.id) < parseInt(sectionId))
        .map((s) => ({ type: s.type, content: s.content }));

      const result = await generateScript({
        creatorId,
        sectionType: section.type,
        selectedHook: selectedHook || undefined,
        previousSections:
          previousSections.length > 0 ? previousSections : undefined,
        videoFormat: videoFormat as "shorts" | "long-form",
      });

      setScriptSections((prev) =>
        prev.map((s) =>
          s.id === sectionId
            ? {
                ...s,
                content: result.content,
                variations: result.variations,
                currentVariationIndex: 0,
                isGenerating: false,
              }
            : s,
        ),
      );

      toast({
        title: "Section Generated",
        description: `${section.title} has been generated successfully.`,
      });
    } catch (error) {
      console.error("Error generating section:", error);
      toast({
        title: "Generation Failed",
        description: `Failed to generate ${section.title}. Please try again.`,
        variant: "destructive",
      });

      setScriptSections((prev) =>
        prev.map((s) =>
          s.id === sectionId ? { ...s, isGenerating: false } : s,
        ),
      );
    }
  };

  const cycleVariation = (sectionId: string, direction: "next" | "prev") => {
    setActiveSection(sectionId);
    setScriptSections((prev) =>
      prev.map((section) => {
        if (section.id === sectionId) {
          const newIndex =
            direction === "next"
              ? (section.currentVariationIndex + 1) % section.variations.length
              : (section.currentVariationIndex -
                  1 +
                  section.variations.length) %
                section.variations.length;
          return {
            ...section,
            content: section.variations[newIndex],
            currentVariationIndex: newIndex,
          };
        }
        return section;
      }),
    );
  };

  const moveToNextSection = async () => {
    const currentIndex = scriptSections.findIndex(
      (section) => section.id === activeSection,
    );
    if (currentIndex < scriptSections.length - 1) {
      const nextSection = scriptSections[currentIndex + 1];
      setActiveSection(nextSection.id);
      setCompletedSections((prev) => new Set([...prev, nextSection.id]));

      // Auto-generate content for the next section if it's empty
      if (!nextSection.content) {
        await generateSectionContent(nextSection.id);
      }
    }
  };

  const exportScript = async () => {
    try {
      // Compile the full script
      const fullScript = scriptSections
        .map((section) => {
          if (section.content) {
            return `${section.title.toUpperCase()}\n${"=".repeat(section.title.length)}\n${section.content}\n`;
          }
          return "";
        })
        .filter(Boolean)
        .join("\n");

      const scriptHeader = `${videoFormat === "shorts" ? "YouTube Shorts" : "Long-Form Video"} Script\nGenerated on ${new Date().toLocaleDateString()}\n\n`;
      const finalScript = scriptHeader + fullScript;

      // Copy to clipboard
      await navigator.clipboard.writeText(finalScript);

      // Download as .txt file
      const blob = new Blob([finalScript], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${videoFormat}-script-${new Date().toISOString().split("T")[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Script Exported",
        description: "Script has been downloaded and copied to clipboard.",
      });
    } catch (error) {
      console.error("Error exporting script:", error);
      toast({
        title: "Export Failed",
        description: "Failed to export script. Please try again.",
        variant: "destructive",
      });
    }
  };

  const progress = (completedSections.size / scriptSections.length) * 100;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!event.target || !(event.target instanceof HTMLElement)) return;

      const isFloatingButton = event.target.closest(
        ".floating-generate-button",
      );
      const isEditor = event.target.closest(".script-editor");

      if (!isFloatingButton && !isEditor) {
        setButtonPosition((prev) => ({ ...prev, visible: false }));
        setSelectedText("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Video format selection screen
  if (!videoFormat) {
    return (
      <div className="p-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <Button variant="ghost" onClick={onBack} className="mb-4">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Hooks
            </Button>
            <h1 className="text-3xl font-bold">Choose Video Format</h1>
            <p className="text-muted-foreground mb-6">
              Select the type of video you want to create to get the right
              script structure
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card
              className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-primary"
              onClick={() => setVideoFormat("shorts")}
            >
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mb-4">
                  <Clock className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">YouTube Shorts</CardTitle>
                <p className="text-muted-foreground">15-60 seconds</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-sm">
                    <strong>Perfect for:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-1 text-muted-foreground">
                      <li>Quick tips and hacks</li>
                      <li>Fast-paced content</li>
                      <li>Viral moments</li>
                      <li>Simple concepts</li>
                    </ul>
                  </div>
                  <div className="text-sm">
                    <strong>Structure:</strong>
                    <div className="mt-1 space-y-1 text-muted-foreground">
                      <div>1. Hook (3 seconds)</div>
                      <div>2. Problem/Question</div>
                      <div>3. Solution/Answer</div>
                      <div>4. Call to Action</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card
              className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-primary"
              onClick={() => setVideoFormat("long-form")}
            >
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                  <Video className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-xl">Long-Form Video</CardTitle>
                <p className="text-muted-foreground">5+ minutes</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-sm">
                    <strong>Perfect for:</strong>
                    <ul className="list-disc list-inside mt-1 space-y-1 text-muted-foreground">
                      <li>In-depth tutorials</li>
                      <li>Detailed reviews</li>
                      <li>Educational content</li>
                      <li>Storytelling</li>
                    </ul>
                  </div>
                  <div className="text-sm">
                    <strong>Structure:</strong>
                    <div className="mt-1 space-y-1 text-muted-foreground">
                      <div>1. Hook → 2. Intro → 3. Problem</div>
                      <div>4. Main Content → 5. Proof</div>
                      <div>6. Transition → 7. Conclusion → 8. CTA</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => setVideoFormat(null)}
            className="mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Format Selection
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold">Script Generation</h1>
            <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm">
              {videoFormat === "shorts" ? (
                <>
                  <Clock className="w-4 h-4" />
                  Shorts
                </>
              ) : (
                <>
                  <Video className="w-4 h-4" />
                  Long-form
                </>
              )}
            </div>
          </div>
          <p className="text-muted-foreground mb-4">
            Complete each section to build your{" "}
            {videoFormat === "shorts" ? "short-form" : "long-form"} script
          </p>
          <Progress value={progress} className="h-2" />
        </div>

        <div className="space-y-4">
          {scriptSections.map((section) => {
            const isActive = section.id === activeSection;
            const isCompleted = completedSections.has(section.id);
            const shouldShow = isActive || isCompleted;

            return shouldShow ? (
              <Card key={section.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {section.title}
                        {section.isGenerating && (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        )}
                      </CardTitle>
                      {section.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {section.description}
                        </p>
                      )}
                    </div>
                    {section.variations.length > 1 && (
                      <span className="text-sm text-muted-foreground">
                        {section.currentVariationIndex + 1} of{" "}
                        {section.variations.length}
                      </span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {section.isGenerating ? (
                    <div className="min-h-[100px] p-4 border rounded-lg flex items-center justify-center">
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Generating {section.title.toLowerCase()}...
                        </p>
                      </div>
                    </div>
                  ) : section.content ? (
                    <>
                      <div
                        className="script-editor min-h-[100px] p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        contentEditable={true}
                        onMouseUp={() => handleSelection(section.id)}
                        onKeyUp={() => handleSelection(section.id)}
                        dangerouslySetInnerHTML={{ __html: section.content }}
                      />
                      {isActive && (
                        <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5 animate-fade-in">
                          <Sparkles className="w-3.5 h-3.5" />
                          Highlight any text to regenerate it
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="min-h-[100px] p-4 border rounded-lg border-dashed flex items-center justify-center">
                      <Button
                        onClick={() => generateSectionContent(section.id)}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        Generate {section.title}
                      </Button>
                    </div>
                  )}

                  {section.variations.length > 1 &&
                    isActive &&
                    !section.isGenerating && (
                      <div className="flex justify-between items-center mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cycleVariation(section.id, "prev")}
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cycleVariation(section.id, "next")}
                        >
                          Next
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    )}
                  {isActive && section.content && !section.isGenerating && (
                    <div className="flex justify-between items-center mt-6">
                      <Button
                        variant="outline"
                        onClick={() => generateSectionContent(section.id)}
                        className="flex items-center gap-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        Regenerate Section
                      </Button>
                      {section.id !==
                        scriptSections[scriptSections.length - 1].id && (
                        <Button onClick={moveToNextSection}>
                          Continue to{" "}
                          {
                            scriptSections[
                              scriptSections.findIndex(
                                (s) => s.id === section.id,
                              ) + 1
                            ].title
                          }
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : null;
          })}
        </div>

        {buttonPosition.visible && (
          <div
            className="floating-generate-button fixed z-50 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-3 flex flex-col gap-2 min-w-[300px]"
            style={{
              left: `${buttonPosition.x}px`,
              top: `${buttonPosition.y}px`,
            }}
          >
            <input
              type="text"
              value={generationGuidance}
              onChange={(e) => setGenerationGuidance(e.target.value)}
              placeholder="Optional: Add guidance for regeneration..."
              className="w-full px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  regenerateSelection();
                }
              }}
            />
            <Button
              size="sm"
              className="flex items-center justify-center space-x-2 w-full"
              onClick={regenerateSelection}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              <span>{isRegenerating ? "Regenerating..." : "Regenerate"}</span>
            </Button>
          </div>
        )}

        {scriptSections.length > 0 &&
          activeSection === scriptSections[scriptSections.length - 1].id &&
          scriptSections.every((section) => section.content.trim() !== "") && (
            <Card className="mt-6">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Script Complete!</h3>
                  <p className="text-muted-foreground mb-4">
                    Your {videoFormat === "shorts" ? "short-form" : "long-form"}{" "}
                    script is ready. You can export it or go back to create
                    another.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <Button variant="outline" onClick={onBack}>
                      Go Back
                    </Button>
                    <Button
                      className="bg-green-500 hover:bg-green-600"
                      onClick={exportScript}
                    >
                      Export Script
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
      </div>
    </div>
  );
};
